import React, { useContext } from "react";
import {
  EuiDescribedFormGroup,
  EuiFormFieldset,
  EuiFormRow,
  EuiRadio,
  EuiSpacer,
  EuiTable,
  EuiTableBody,
  EuiTableHeader,
  EuiTableHeaderCell,
  EuiTableRow,
  EuiTableRowCell,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { EvaluationContext, useAnketa } from "../context_provider";
import {
  type LikertQuestion,
  type TextMessage,
  type Hierarchical,
} from "anketa-core";
import { MatrixCardProps } from "src/questionnaires/types";
import { useAnketaI18n } from "src/internationalization";

export const MatrixCard = ({
  matrix,
  stripes,
}: MatrixCardProps): JSX.Element => {
  const ctx = useAnketa();
  const evalCtx = useContext(EvaluationContext);
  const { translate } = useAnketaI18n();

  // Early returns for visibility and empty states
  if (!matrix.visible) return <div />;

  const childrenArray = Array.from(matrix.children.values());
  if (childrenArray.length === 0) {
    console.warn("Empty matrix", matrix.id);
    return (
      <EuiText color="subdued">
        {translate("anketa.questionnaire.card.matrix.empty", {
          defaultMessage: "Empty matrix",
        })}
      </EuiText>
    );
  }

  const handleSetValue = (question: LikertQuestion, val: string) => {
    // Only update if value actually changed or raw is null
    if (question.fact.raw === null || question.fact.toString() !== val) {
      question.setFactValue(parseInt(val));
      ctx.reevaluate();
      evalCtx.nextEvaluationId();
    }
  };

  // Extract table logic into a clean, memoized render function
  // The title/description are shown either by the described form group (when a
  // description exists) or by the form row - never by both, as before.
  const renderTable = (withLabel: boolean) => {
    // Assume all rows have the same options; get options from the first child
    const firstQuestion = childrenArray[0] as LikertQuestion;
    const options = firstQuestion.getOptions(ctx);
    const optionKeys = Array.from(options.keys());

    return (
      <EuiFormRow
        label={withLabel ? ctx.i18nContext.render(matrix.title) : undefined}
        fullWidth
      >
        <EuiFormFieldset>
          <EuiTable
            compressed={stripes}
            tableLayout="fixed"
            className={stripes ? "aqMatrix--striped" : undefined}
          >
            <EuiTableHeader>
              <EuiTableHeaderCell key="header-empty">&nbsp;</EuiTableHeaderCell>
              {optionKeys.map((key) => (
                <EuiTableHeaderCell key={`header-${key}`} align="center">
                  {ctx.i18nContext.render(options.get(key))}
                </EuiTableHeaderCell>
              ))}
            </EuiTableHeader>

            <EuiTableBody>
              {childrenArray.map((item) => {
                const q = item as LikertQuestion;

                // Stripes come from questionnaire-theme.css so they follow
                // the colour mode (was a hardcoded light grey).
                return (
                  <EuiTableRow key={q.path}>
                    <EuiTableRowCell>
                      <EuiText size="s">
                        <strong>{ctx.i18nContext.render(q.title)}</strong>
                      </EuiText>
                    </EuiTableRowCell>

                    {optionKeys.map((key) => (
                      <EuiTableRowCell key={`${q.path}-${key}`} align="center">
                        <EuiRadio
                          id={`${q.path}-${key}`}
                          name={q.path}
                          checked={q.fact.toString() === key}
                          onChange={() => handleSetValue(q, key)}
                          aria-label={`${ctx.i18nContext.render(q.title)}: ${ctx.i18nContext.render(options.get(key))}`}
                        />
                      </EuiTableRowCell>
                    ))}
                  </EuiTableRow>
                );
              })}
            </EuiTableBody>
          </EuiTable>
        </EuiFormFieldset>
      </EuiFormRow>
    );
  };

  // Main Return Logic
  if (matrix.description) {
    return (
      <>
        <EuiDescribedFormGroup
          title={
            <EuiTitle size="xs">
              <h4>{ctx.i18nContext.render(matrix.title)}</h4>
            </EuiTitle>
          }
          description={ctx.i18nContext.render(matrix.description)}
          fullWidth
        >
          {renderTable(false)}
        </EuiDescribedFormGroup>
        <EuiSpacer size="l" />
      </>
    );
  }

  return renderTable(true);
};
